<template>
  <div class="file-loader-grouped-list">
    <!-- Loading state -->
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
      aria-live="polite"
      aria-label="Loading file data"
    />

    <!-- Empty state -->
    <div v-if="!loading && Object.keys(groupedFiles).length === 0" class="text-center py-12">
      <v-icon size="64" color="grey lighten-1">mdi-file-document-outline</v-icon>
      <div class="text-h6 grey--text mt-4">No files found</div>
      <div class="text-body-2 grey--text">Files will appear here once they are processed</div>
    </div>

    <!-- Grouped list -->
    <div v-if="!loading && Object.keys(groupedFiles).length > 0" class="grouped-list-container">
      <div
        v-for="(group, date) in groupedFiles"
        :key="date"
        class="date-group mb-4"
      >
        <!-- Date header -->
        <div class="date-header d-flex align-center justify-space-between mb-2">
          <h3 class="text-h6 font-weight-bold">
            {{ formatDateHeader(date) }}
          </h3>
          <v-chip small outlined class="file-count-chip">
            {{ group.length }} {{ group.length === 1 ? 'file' : 'files' }}
          </v-chip>
        </div>

        <!-- Files for this date -->
        <v-card outlined class="group-card">
          <v-list>
            <template v-for="(file, index) in group">
              <v-list-item
                :key="file.fileLoaderGuid"
                class="file-list-item"
                :class="{ 'file-list-item--failed': file.loadStatus === 'failed' }"
                :aria-label="`File: ${file.fileName}, Status: ${file.loadStatus}`"
                :data-testid="`grouped-file-${file.fileLoaderGuid}`"
              >
                <!-- File icon -->
                <v-list-item-avatar>
                  <v-icon
                    :color="file.loadStatus === 'failed' ? 'error' : 'primary'"
                  >
                    {{ file.loadStatus === 'failed' ? 'mdi-close-circle' : 'mdi-check-circle' }}
                  </v-icon>
                </v-list-item-avatar>

                <!-- File content -->
                <v-list-item-content>
                  <!-- File name and timestamp -->
                  <v-list-item-title class="file-name-title">
                    {{ file.fileName }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-caption">
                    {{ formatDateTime(file.loadedDate) }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <!-- File type badge -->
                <div class="file-type-column">
                  <file-loader-type-badge :file-type="file.fileType" />
                </div>

                <!-- Stats -->
                <div class="file-stats d-flex align-center">
                  <div class="stat-column">
                    <div
                      class="stat-value"
                      :class="file.rowsAccepted > 0 ? 'success--text' : 'grey--text'"
                    >
                      {{ file.rowsAccepted.toLocaleString() }}
                    </div>
                    <div class="stat-label">Accepted</div>
                  </div>
                  <div class="stat-column">
                    <div
                      class="stat-value"
                      :class="file.rowsRejected > 0 ? 'error--text' : 'grey--text'"
                    >
                      {{ file.rowsRejected.toLocaleString() }}
                    </div>
                    <div class="stat-label">Rejected</div>
                  </div>
                  <div class="stat-column">
                    <div
                      class="stat-value"
                      :class="file.submissionsCreated > 0 ? 'primary--text' : 'grey--text'"
                    >
                      {{ file.submissionsCreated.toLocaleString() }}
                    </div>
                    <div class="stat-label">Submissions</div>
                  </div>
                  <div class="stat-column">
                    <div class="stat-value grey--text text--darken-1">
                      {{ formatDuration(file.durationSeconds) }}
                    </div>
                    <div class="stat-label">Duration</div>
                  </div>
                </div>
              </v-list-item>

              <v-divider v-if="index < group.length - 1" :key="`divider-${file.fileLoaderGuid}`" />
            </template>
          </v-list>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import type { PropType } from 'vue'
import FileLoaderTypeBadge from './FileLoaderTypeBadge.vue'
import type { FileLoadJob } from './types'
import { formatDate, formatDuration } from '../../utils/formatters'

export default Vue.extend({
  name: 'FileLoaderGroupedList',

  components: {
    FileLoaderTypeBadge
  },

  props: {
    /** Array of file load jobs */
    files: {
      type: Array as PropType<FileLoadJob[]>,
      required: true
    },
    /** Loading state */
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    groupedFiles(): Record<string, FileLoadJob[]> {
      const groups: Record<string, FileLoadJob[]> = {}

      this.files.forEach((file) => {
        const date = new Date(file.loadedDate)
        const dateKey = date.toISOString().split('T')[0]

        if (!groups[dateKey]) {
          groups[dateKey] = []
        }
        groups[dateKey].push(file)
      })

      // Sort groups by date descending
      const sortedGroups: Record<string, FileLoadJob[]> = {}
      Object.keys(groups)
        .sort((a, b) => b.localeCompare(a))
        .forEach((key) => {
          // Sort files within group by time descending
          sortedGroups[key] = groups[key].sort((a, b) =>
            new Date(b.loadedDate).getTime() - new Date(a.loadedDate).getTime()
          )
        })

      return sortedGroups
    }
  },

  methods: {
    formatDateHeader(dateString: string): string {
      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return 'Invalid date'
        return date.toLocaleDateString('en-GB', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        console.error('Date formatting error:', e)
        return 'Invalid date'
      }
    },

    formatDateTime(dateString: string): string {
      return formatDate(dateString, 'full')
    },

    formatDuration
  }
})
</script>

<style scoped>
.file-loader-grouped-list {
  width: 100%;
}

.grouped-list-container {
  width: 100%;
}

.date-group {
  width: 100%;
}

.date-header {
  padding: 6px 0;
  border-bottom: 2px solid var(--border-subtle, #e5e7eb);
}

.date-header h3 {
  color: var(--text-primary, #1f2937);
}

.file-count-chip {
  font-weight: 600;
}

.group-card {
  border-radius: var(--radius);
  overflow: hidden;
}

.file-list-item {
  min-height: 56px;
  padding: 8px 12px;
  transition: background-color 0.2s ease;
}

.file-list-item:hover {
  background-color: var(--interactive-hover, rgba(0, 0, 0, 0.04));
}

.file-list-item--failed {
  border-left: 4px solid var(--status-error, #dc2626);
  background-color: var(--status-error-light, #fef2f2);
}

.file-name-title {
  font-weight: 600;
  color: var(--text-primary, #1f2937);
}

.file-stats {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  margin-left: auto;
}

.stat-column {
  text-align: center;
  width: 70px;
}

.stat-value {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 0.625rem;
  color: var(--text-secondary, #6b7280);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.1;
}

.file-type-column {
  flex-shrink: 0;
  width: 110px;
  margin-right: 12px;
  text-align: center;
}

/* Dark mode support */
.theme--dark .date-header {
  border-bottom-color: var(--border-subtle-dark, #374151);
}

.theme--dark .date-header h3 {
  color: var(--text-primary-dark, #f9fafb);
}

.theme--dark .file-name-title {
  color: var(--text-primary-dark, #f9fafb);
}

.theme--dark .file-list-item:hover {
  background-color: var(--interactive-hover-dark, rgba(255, 255, 255, 0.04));
}

.theme--dark .file-list-item--failed {
  background-color: var(--status-error-light-dark, rgba(220, 38, 38, 0.1));
}
</style>
